import _ from 'lodash'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useCreate } from 'react-admin'
import Papa from 'papaparse'
import dataset from '../resources/import-master-data/dataset'

export const useImportMasterData = ({
  importType,
  productType,
}: {
  importType: string
  productType: string
}): {
  handleClose: () => void
  handleSubmit: (e) => Promise<void>
  loading: boolean
  progress: number
  progressDialog: boolean
  importResultLists: object[]
} => {
  const record = useRef(new Map())

  const [loading, setLoading] = useState(false)
  // const [progressDialog, setProgressDialog] = useState<boolean>(true)
  const [progressDialog, setProgressDialog] = useState<boolean>(false)
  const [progress, setProgress] = useState<number>(0.0)
  const [currentIndex, setCurrentIndex] = useState<number>(-1)

  const [importResultLists, setImportResultLists] = useState([])

  const csvFileToArray = (string): object[] => {
    const result = Papa.parse(string, { header: true })

    return _.map(result?.data, (o) => {
      return _.transform(
        o,
        (result, value, key) => {
          if (_.includes(value, ';')) {
            _.set(result, [key], _.split(value, ';'))
          } else {
            _.set(result, key, value)
          }
        },
        {},
      )
    })
  }

  const upImportResult = useCallback(
    async (isSuccess: boolean, errorMessage?: string): Promise<void> => {
      const dataLists = record.current.get('dataLists')
      const p =
        dataLists?.[currentIndex] && _.mapKeys(dataLists?.[currentIndex], (o, k) => _.toLower(k))

      setCurrentIndex(currentIndex + 1)
      setImportResultLists([
        {
          record: { ...p, id: `${p?.brand} ${p?.model} ${p?.sub_model || ''}` },
          isSuccess,
          errorMessage,
        },
        ...importResultLists,
      ])
    },
    [currentIndex, importResultLists],
  )

  const onError = useCallback(
    (error): void => {
      upImportResult(false, error?.response?.data?.message)
    },
    [upImportResult],
  )

  const onSuccess = useCallback(async (): Promise<void> => {
    upImportResult(true)
  }, [upImportResult])

  const [create] = useCreate(undefined, undefined, { onError, onSuccess })

  const run = useCallback(async (): Promise<void> => {
    const images = record.current.get('images')
    const dataLists = record.current.get('dataLists')
    const dataListsCount = record.current.get('dataListsCount')

    const p =
      dataLists?.[currentIndex] && _.mapKeys(dataLists?.[currentIndex], (o, k) => _.toLower(k))
    if (p) {
      const createData = _.pick(p, dataset?.[importType]?.[productType]?.keyList || [])
      if (dataset?.[importType]?.[productType]?.needPictures) {
        _.forEach(dataset?.[importType]?.[productType]?.imageKey, (ik) => {
          _.forEach(ik, (o, k) => {
            _.set(createData, [k], images?.[p?.[o]])
          })
        })
      }

      await create(dataset?.[importType]?.[productType]?.resourceKey, {
        data: dataset?.[importType]?.[productType]?.needPictures
          ? { encType: 'multipart/form-data', ...createData }
          : createData,
      })

      setProgress(((currentIndex + 1) * 100) / dataListsCount)
    }
  }, [create, currentIndex, importType, productType])
  // }, [currentIndex])

  const handleClose = useCallback((): void => {
    setProgress(0)
    setLoading(false)
    setProgressDialog(false)

    record.current.delete('images')
    record.current.delete('dataLists')
    record.current.delete('dataListsCount')
    setCurrentIndex(-1)
    setImportResultLists([])
  }, [])

  const handleSubmit = useCallback(
    async (e): Promise<void> => {
      if (!dataset?.[importType]?.[productType]) {
        handleClose()
        return
      }
      const fileReader = new FileReader()
      setLoading(true)
      setProgressDialog(true)
      fileReader.onload = function (event): void {
        const images = _.mapKeys(e?.images, (o) => {
          return o?.title
        })
        const text = event.target.result
        const dataLists = csvFileToArray(text)
        record.current.set('images', images)
        record.current.set('dataLists', dataLists)
        record.current.set('dataListsCount', _.size(dataLists))
        setCurrentIndex(0)
      }
      fileReader.readAsText(e?.csv?.rawFile)
    },
    [handleClose, importType, productType],
  )

  useEffect(() => {
    if (currentIndex > -1) {
      run()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex])

  return { loading, progress, progressDialog, importResultLists, handleSubmit, handleClose }
}
